import React, { FormEvent, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  TextInput,
  RadioGroup,
  Radio,
  FileUpload,
  Button,
  Divider,
  PasswordInput,
  Alert,
  useToast,
  Dropdown,
  SelectInput,
  DropdownOverlay,
  ActionList,
  ActionListItem,
  BoxProps,
} from '@razorpay/blade/components';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
} from 'components/WizardViews/components';
import { useFormWithProvider } from 'forms';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { useSelector } from 'react-redux';
import { BankAccountFormData } from './types';
import {
  bankAccountValidationSchemaWithProof,
  bankAccountValidationSchemaForAdmin,
  bankAccountValidationSchema,
} from './utils';
import { VerifyingBankAccountModal } from './VerifyingBankAccountModal';
import { OtpModal } from './OtpModal';
import { InvalidBankDetailsModal } from './InvalidBankDetailsModal';
import { VerificationFailedModal } from './VerificationFailedModal';
import { useIsViewEditBankAccountAllowed } from '../hooks/useIsViewEditBankAccountAllowed';
import { useMutation, useQueryClient } from 'react-query';
import {
  BANK_PROOF_UPLOAD,
  BankAccountApprovalStatus,
  BankAccountRequestDetails,
  BankAccountVerificationStatus,
  FETCH_BANK_ACCOUNTS,
  SEND_FOR_APPROVAL,
  SEND_OTP,
  VERIFY_ACCOUNT,
  VerifyAccountPayload,
} from 'api/employeeBankAccount';
import api from 'api';
import { useUserId } from '../hooks/useUserId';
import { SendForApprovalModal } from './SendForApprovalModal';
import { NameMismatchModal } from './NameMismatchModal';
import { AppError, getAppError } from 'utils/AppError';
import { OTP_VERIFICATION_ERROR } from 'constants/otp';
import {
  bankAccountApprovalStatus,
  bankAccountProofType,
  bankAccountVerificationStatus,
  bankFormDataInitialState,
  bankProofTypeToLabel,
} from '../data';
import { parseError } from 'utils/Api';

export const AccountDetailsStep = ({
  onBackClick,
  onSuccess,
  showLabel = true,
  StepContentWrapperProps,
  StepActionsWrapperProps,
  employeeName,
  isPrefilled = false,
}: {
  onBackClick?: () => void;
  onSuccess?: () => void;
  showLabel?: boolean;
  StepContentWrapperProps?: Partial<BoxProps>;
  StepActionsWrapperProps?: Partial<BoxProps>;
  employeeName: string;
  isPrefilled?: boolean;
}) => {
  const toast = useToast();
  const userId = useUserId();
  const queryClient = useQueryClient();
  const { isUserAdmin } = useIsViewEditBankAccountAllowed();
  const isRxCurrentAccountMerchant = !!useSelector(loggedInUserSelectors.currentOrganization)
    ?.isRxCurrentAccountMerchant;
  const currentOrganization = useSelector(loggedInUserSelectors.currentOrganization);

  const { validate, formData, setFormData, register, clearFieldError, isSubmitting } =
    useFormWithProvider<BankAccountFormData>();

  const [shouldRetryWithProof, setShouldRetryWithProof] = useState(false);

  const [areAccountFieldsLocked, setAreAccountFieldsLocked] = useState(isPrefilled);

  const areAccountFieldsDisabled = isSubmitting || areAccountFieldsLocked;

  const [openModalType, setOpenModalType] = React.useState<
    'verifying' | 'otp' | 'invalidBank' | 'apiFailure' | 'sendForApproval' | 'nameMismatch'
  >();

  const [verificationErrorMessage, setVerificationErrorMessage] = useState<string>();

  const isAutoApprovalEnabled = !!currentOrganization?.isBankAccountAutoVerificationEnabled;
  const uploadProofForVerification =
    !!currentOrganization?.isProofRequiredForBankAccountVerification;

  const showProofField = isRxCurrentAccountMerchant
    ? !isUserAdmin && shouldRetryWithProof
    : !isUserAdmin && uploadProofForVerification;

  const canVerifyAccount = isRxCurrentAccountMerchant && !shouldRetryWithProof;

  const handleSuccess = () => {
    onSuccess?.();
  };

  const mutateSendOtp = useMutation({
    mutationKey: SEND_OTP,
    mutationFn: api.employeeBankAccount.sendOtp,
    onSuccess: () => {
      toast.show({
        content: 'OTP sent successfully',
        color: 'positive',
      });
      setOpenModalType('otp');
    },
    onError: (e: typeof AppError) => {
      toast.show({
        content: parseError(e),
        color: 'negative',
      });
    },
  });

  const onVerifyOrAddAccountFailure = (e?: typeof AppError) => {
    setAreAccountFieldsLocked(false);
    const isInvalidOtpError = e && getAppError(e).getDetails().code === OTP_VERIFICATION_ERROR;

    if (isInvalidOtpError) {
      setOpenModalType('otp');
      return;
    }

    if (canVerifyAccount) {
      setOpenModalType('apiFailure');
    } else {
      toast.show({
        content: e ? parseError(e) : 'Something went wrong, please try again',
        color: 'negative',
      });
      setOpenModalType(undefined);
    }
  };

  const onVerifyOrAddAccountSuccess = ({
    verification_status,
    approval_status,
  }: {
    verification_status: BankAccountVerificationStatus;
    approval_status: BankAccountApprovalStatus;
  }) => {
    setAreAccountFieldsLocked(false);
    if (approval_status === bankAccountApprovalStatus.pending) {
      toast.show({
        content: 'Bank Account Change Request Submitted',
        color: 'information',
      });
      queryClient.invalidateQueries([FETCH_BANK_ACCOUNTS]);
      handleSuccess();
      return;
    }

    if (
      approval_status === bankAccountApprovalStatus.auto_approved ||
      approval_status === bankAccountApprovalStatus.approved
    ) {
      toast.show({
        content: 'Bank Account Added Successfully',
        color: 'positive',
      });
      queryClient.invalidateQueries([FETCH_BANK_ACCOUNTS]);
      handleSuccess();
      return;
    }

    if (verification_status === bankAccountVerificationStatus.mismatch) {
      if (isUserAdmin) {
        setOpenModalType('nameMismatch');
      } else {
        setOpenModalType('sendForApproval');
      }
      return;
    }
    if (verification_status === bankAccountVerificationStatus.invalid) {
      setOpenModalType('invalidBank');
      return;
    }
    if (verification_status === bankAccountVerificationStatus.failed) {
      setShouldRetryWithProof(true);
      setOpenModalType('apiFailure');
      return;
    }

    onVerifyOrAddAccountFailure();
    return;
  };

  const mutateVerifyAccount = useMutation({
    mutationKey: VERIFY_ACCOUNT,
    mutationFn: api.employeeBankAccount.verifyAccount,
    onSuccess: ({ verification_status, approval_status }) => {
      onVerifyOrAddAccountSuccess({ verification_status, approval_status });
    },
    onError: (e: typeof AppError) => {
      onVerifyOrAddAccountFailure(e);
    },
  });

  const getBankAccountApiPayload = (file_id?: string) => {
    const payload: Omit<VerifyAccountPayload, 'two_factor_auth_otp'> = {
      account_number: formData.accountNumber || '',
      ifsc: formData.ifscCode || '',
      user_id: userId,
      is_retry_with_proof: shouldRetryWithProof,
    };

    const paymentMode = Array.isArray(formData.paymentMode)
      ? formData.paymentMode[0]
      : formData.paymentMode;

    if (isUserAdmin && paymentMode) {
      payload.payment_mode = paymentMode;
    }

    if (file_id) {
      payload.file_id = file_id;
      payload.file_type = formData.bankProofType;
    }

    if (!canVerifyAccount) {
      payload.beneficiary_name = formData.beneficiaryName;
    }

    return payload;
  }; //this should be same as the payload in send otp and submit otp call

  const generateOtp = (file_id?: string) => {
    mutateSendOtp.mutate(getBankAccountApiPayload(file_id));
  };

  const mutateAddBankProof = useMutation({
    mutationKey: BANK_PROOF_UPLOAD,
    mutationFn: api.employeeBankAccount.addBankProof,
    onSuccess: ({ file_id }) => {
      generateOtp(file_id);
    },
    onError: (e: typeof AppError) => {
      toast.show({
        content: parseError(e),
        color: 'negative',
      });
    },
  });

  const getShouldRequestApproval = () => {
    if (isUserAdmin) {
      return false;
    }
    if (!isRxCurrentAccountMerchant) {
      return true;
    }
    if (!isAutoApprovalEnabled) {
      return true;
    }
    if (shouldRetryWithProof) {
      return true;
    }
    if (mutateVerifyAccount.data?.verification_status === bankAccountVerificationStatus.mismatch) {
      return true;
    }
    return false;
  };

  const shouldRequireApproval = getShouldRequestApproval();

  const isSubmitButtonDisabled =
    mutateAddBankProof.isLoading || mutateSendOtp.isLoading || mutateVerifyAccount.isLoading;

  const clearErrorOnFocus = (fieldName: keyof BankAccountFormData) => clearFieldError(fieldName);

  const handleSubmit = async (e: FormEvent) => {
    e.stopPropagation();
    e.preventDefault();

    try {
      let validationSchema = bankAccountValidationSchema;
      if (isUserAdmin && !canVerifyAccount) {
        validationSchema = bankAccountValidationSchemaForAdmin;
      }
      if (showProofField) {
        validationSchema = bankAccountValidationSchemaWithProof;
      }

      if (validate(validationSchema) && formData.accountNumber && formData.ifscCode) {
        setVerificationErrorMessage(undefined);

        const file = Array.isArray(formData.bankProofFile)
          ? formData.bankProofFile[0]
          : formData.bankProofFile;

        if (file) {
          mutateAddBankProof.mutateAsync({
            file,
            proofType: formData.bankProofType as BankAccountRequestDetails['attachment_type'],
          });
        } else {
          generateOtp();
        }
      }
    } catch (e) {
      toast.show({
        content: 'Something went wrong, please try again',
        color: 'negative',
      });
    }
  };

  const closeModal = () => {
    setOpenModalType(undefined);
  };

  const onOtpSubmit = (otp: string) => {
    if (canVerifyAccount) {
      setOpenModalType('verifying');
    }
    mutateVerifyAccount.mutate({
      ...getBankAccountApiPayload(mutateAddBankProof.data?.file_id),
      two_factor_auth_otp: otp,
    });
  };

  const onResendOtp = () => {
    generateOtp(mutateAddBankProof.data?.file_id);
  };

  const otpModalBankDetails = {
    ...formData,
    beneficiaryName:
      (canVerifyAccount ? mutateVerifyAccount.data?.beneficiary_name : formData?.beneficiaryName) ||
      '',
  };

  const onContinueInvalidBank = () => {
    setVerificationErrorMessage(
      'The details you provided are incorrect. Please enter valid bank account information to proceed.',
    );
    setFormData(bankFormDataInitialState);
    queryClient.invalidateQueries([VERIFY_ACCOUNT]);
    queryClient.invalidateQueries([BANK_PROOF_UPLOAD]);
    queryClient.invalidateQueries([SEND_OTP]);
    queryClient.invalidateQueries([SEND_FOR_APPROVAL]);
    closeModal();
  };

  const onContinueRetryWithProof = () => {
    setVerificationErrorMessage(
      isUserAdmin
        ? 'Since we are not able to verify the bank account details provided, try again later or update details without penny testing.'
        : 'Since we are not able to verify the bank account details provided, upload proof or edit details if they seem incorrect',
    );
    closeModal();
  };

  const onDismissFlow = () => {
    closeModal();
    onBackClick?.();
    setAreAccountFieldsLocked(false);
    setFormData(bankFormDataInitialState);
  };

  return (
    <>
      <FormWrapper>
        <StepContentWrapper {...StepContentWrapperProps} noBgIllustration>
          <Box paddingY="spacing.7">
            {!!verificationErrorMessage && (
              <Alert
                onDismiss={() => setVerificationErrorMessage(undefined)}
                isFullWidth
                color="negative"
                marginBottom="spacing.7"
                title="Bank Verification Failed"
                description={verificationErrorMessage}
              />
            )}

            <Box display="flex" gap="spacing.6">
              {showLabel && (
                <Box width="45%">
                  <Heading size="small" weight="semibold" color="surface.text.gray.normal">
                    Bank Account Details
                  </Heading>
                  <Text size="small" weight="regular" color="surface.text.gray.muted">
                    Add bank account details linked to {employeeName}. Payments will be transferred
                    to this account.
                  </Text>
                </Box>
              )}

              <Box flexGrow={1} display="flex" flexDirection="column" gap="spacing.7">
                <PasswordInput
                  label="Account Number"
                  placeholder="**********"
                  showRevealButton={false}
                  {...register('accountNumber')}
                  isDisabled={areAccountFieldsDisabled}
                  necessityIndicator="required"
                  onFocus={() => clearErrorOnFocus('accountNumber')}
                />

                <TextInput
                  label="Confirm Account Number"
                  placeholder="**********"
                  {...register('confirmAccountNumber')}
                  isDisabled={areAccountFieldsDisabled}
                  onFocus={() => clearErrorOnFocus('confirmAccountNumber')}
                  necessityIndicator="required"
                />

                <TextInput
                  label="IFSC Code"
                  placeholder="IDIB000419"
                  {...register('ifscCode')}
                  isDisabled={areAccountFieldsDisabled}
                  onFocus={() => clearErrorOnFocus('ifscCode')}
                  necessityIndicator="required"
                  helpText="This is an 11-digit code found on the top left corner of a cheque leaf."
                />

                {!canVerifyAccount && (
                  <TextInput
                    label="Account Holder Name"
                    {...register('beneficiaryName')}
                    onFocus={() => clearErrorOnFocus('beneficiaryName')}
                    necessityIndicator="required"
                  />
                )}

                {isUserAdmin && (
                  <Dropdown>
                    <SelectInput
                      label="Payment Mode"
                      placeholder="Default"
                      size="medium"
                      value={formData.paymentMode}
                      {...register('paymentMode', 'select')}
                    />
                    <DropdownOverlay>
                      <ActionList>
                        <ActionListItem title="IMPS" value="IMPS" />
                        <ActionListItem title="NEFT" value="NEFT" />
                      </ActionList>
                    </DropdownOverlay>
                  </Dropdown>
                )}
              </Box>
            </Box>

            {!isUserAdmin && !canVerifyAccount && (
              <>
                {showLabel && <Divider marginTop="spacing.7" />}

                <Box display="flex" gap="spacing.6" marginTop="spacing.7">
                  {showLabel && (
                    <Box width="45%">
                      <Heading size="small" weight="semibold" color="surface.text.gray.normal">
                        Upload Proof Of Bank Account
                      </Heading>
                      <Text size="small" weight="regular" color="surface.text.gray.muted">
                        We are not able to verify your account details. Please upload proof before
                        sending for approval.
                      </Text>
                    </Box>
                  )}
                  <Box flexGrow={1} display="flex" flexDirection="column" gap="spacing.7">
                    <RadioGroup
                      label="Select any one"
                      {...register('bankProofType', 'radiogroup')}
                      necessityIndicator="required"
                      size="small">
                      <Radio value={bankAccountProofType.cheque}>Cancelled Cheque</Radio>
                      <Radio value={bankAccountProofType.bank_statement}>Bank Statement</Radio>
                    </RadioGroup>

                    <FileUpload
                      label={
                        bankProofTypeToLabel[
                          formData.bankProofType as keyof typeof bankProofTypeToLabel
                        ]
                      }
                      uploadType="single"
                      accept=".jpg, .png, .pdf, .jpeg"
                      {...register('bankProofFile', 'fileupload')}
                      necessityIndicator="required"
                      helpText="You can upload upto 5 files. Max size 5MB."
                    />
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </StepContentWrapper>
        <StepActionsWrapper {...StepActionsWrapperProps}>
          {onBackClick ? (
            <Button variant="tertiary" isDisabled={isSubmitButtonDisabled} onClick={onBackClick}>
              Back
            </Button>
          ) : (
            <span />
          )}
          <Button isLoading={isSubmitButtonDisabled} onClick={handleSubmit} type="submit">
            {shouldRequireApproval ? 'Send for approval' : 'Continue'}
          </Button>
        </StepActionsWrapper>
      </FormWrapper>
      <OtpModal
        isOpen={openModalType === 'otp'}
        onDismiss={closeModal}
        onContinue={onOtpSubmit}
        isLoading={isSubmitButtonDisabled}
        onResendOtp={onResendOtp}
        shouldRequireApproval={shouldRequireApproval}
        bankAccountDetails={otpModalBankDetails}
        skipPennyTestingAndApproval={shouldRetryWithProof && isUserAdmin}
        otpError={mutateVerifyAccount.error}
      />
      <VerifyingBankAccountModal
        isOpen={openModalType === 'verifying'}
        accountNumber={formData.accountNumber}
        employeeName={employeeName}
      />
      <InvalidBankDetailsModal
        isOpen={openModalType === 'invalidBank'}
        onDismiss={onDismissFlow}
        onContinue={onContinueInvalidBank}
        accountDetails={{
          accountNumber: mutateVerifyAccount.data?.account_number || '',
          ifscCode: mutateVerifyAccount.data?.ifsc || '',
        }}
      />
      <VerificationFailedModal
        isOpen={openModalType === 'apiFailure'}
        onDismiss={onDismissFlow}
        onContinue={onContinueRetryWithProof}
        error={mutateVerifyAccount.error}
        canRetryUpdatingBankAccount={shouldRetryWithProof}
      />
      <SendForApprovalModal
        isOpen={openModalType === 'sendForApproval'}
        onDismiss={onDismissFlow}
        accountDetails={{
          beneficiaryName: mutateVerifyAccount.data?.beneficiary_name || '',
          accountNumber: mutateVerifyAccount.data?.account_number || '',
          ifscCode: mutateVerifyAccount.data?.ifsc || '',
          bankAccountId: mutateVerifyAccount.data?.id || '',
        }}
      />
      <NameMismatchModal
        isOpen={openModalType === 'nameMismatch'}
        onDismiss={onDismissFlow}
        accountDetails={{
          beneficiaryName: mutateVerifyAccount.data?.beneficiary_name || '',
          employeeName: employeeName,
          bankAccountId: mutateVerifyAccount.data?.id || '',
        }}
      />
    </>
  );
};
